/**
 * OOXML Helper Utility (F-12)
 *
 * Provides format-preserving text replacement in Word documents by working
 * with the OOXML (Office Open XML) representation directly.
 *
 * Instead of insertText() (which strips formatting) or insertHtml() (which
 * replaces formatting), this module:
 *   1. Reads the OOXML of the selected range
 *   2. Replaces text content within <w:t> nodes while preserving <w:rPr> run properties
 *   3. Writes back via insertOoxml()
 *
 * Gracefully falls back if the OOXML APIs are not available (e.g., Word Online).
 */

/**
 * Check if OOXML APIs are available in the current Word environment.
 */
export const isOoxmlSupported = (): boolean => {
    try {
        return typeof Word !== 'undefined' && typeof Word.run === 'function';
    } catch {
        return false;
    }
};

/**
 * Get the OOXML string for a given range.
 */
export const getSelectionOoxml = async (
    context: Word.RequestContext,
    range: Word.Range
): Promise<string | null> => {
    try {
        const ooxml = range.getOoxml();
        await context.sync();
        return ooxml.value;
    } catch (err) {
        console.warn('[ooxmlHelper] Could not get OOXML:', err);
        return null;
    }
};

const WNS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

/** Escape characters that are special in XML text content. */
const escapeXml = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Escape characters that are special in XML attribute values. */
const escapeXmlAttribute = (s: string): string =>
    escapeXml(s).replace(/"/g, '&quot;');

/**
 * Ensure xml:space="preserve" on a <w:t> open tag.
 * Works on strings like `<w:t>` or `<w:t xml:space="preserve">`.
 */
const withPreserve = (openTag: string): string =>
    openTag.includes('xml:space') ? openTag : openTag.replace(/(\s*\/?>)$/, ' xml:space="preserve">');

/** Always return a fresh regex instance to avoid lastIndex state issues. */
const wTRegex = () => /(<w:t(?:\s[^>]*)?>)([\s\S]*?)(<\/w:t>)/g;

/**
 * Serialise a run's rPr element for equality comparison ONLY.
 * XMLSerializer is only used here for reading — never for the output string.
 */
const serializeRpr = (run: Element): string => {
    const rPr = run.getElementsByTagNameNS(WNS, 'rPr')[0];
    return rPr ? new XMLSerializer().serializeToString(rPr) : '';
};

const applyExplicitFontToRuns = (xmlString: string, fontName: string): string => {
    const trimmedFontName = fontName.trim();
    if (!trimmedFontName) return xmlString;

    const escapedFontName = escapeXmlAttribute(trimmedFontName);
    const fontTag =
        `<w:rFonts w:ascii="${escapedFontName}"` +
        ` w:hAnsi="${escapedFontName}"` +
        ` w:cs="${escapedFontName}"` +
        ` w:eastAsia="${escapedFontName}"/>`;

    // Expand self-closing <w:rPr/> first. The paired regex below would otherwise
    // accept `<w:rPr/>` as an open tag and pair it with the NEXT run's </w:rPr>,
    // emitting <w:rFonts> as a direct child of <w:r> — schema-invalid OOXML that
    // makes insertOoxml throw and loses the edit.
    const withPairedRunProperties = xmlString.replace(
        /<w:rPr\b([^>]*?)\s*\/>/g,
        (_match, attributes) => `<w:rPr${attributes}></w:rPr>`
    );

    const withUpdatedRunProperties = withPairedRunProperties.replace(
        /(<w:rPr\b[^>]*>)([\s\S]*?)(<\/w:rPr>)/g,
        (_match, openTag, innerXml, closeTag) => {
            const withoutFonts = innerXml.replace(/<w:rFonts\b[^>]*\/>/g, '');
            return `${openTag}${fontTag}${withoutFonts}${closeTag}`;
        }
    );

    return withUpdatedRunProperties.replace(
        /(<w:r\b[^>]*>)([\s\S]*?)(<\/w:r>)/g,
        (match, openTag, innerXml, closeTag) => {
            if (!innerXml.includes("<w:t")) return match;
            if (innerXml.includes("<w:rPr")) return match;
            return `${openTag}<w:rPr>${fontTag}</w:rPr>${innerXml}${closeTag}`;
        }
    );
};

/**
 * Split newText into N slices proportional to originalWordCounts, at word boundaries.
 * The last slice absorbs any remainder words.
 */
const distributeByWordCount = (newText: string, counts: number[]): string[] => {
    const totalOriginal = counts.reduce((a, b) => a + b, 0);
    const words = newText.match(/\S+\s*/g) ?? [];
    if (totalOriginal === 0 || words.length === 0)
        return counts.map((_, i) => (i === 0 ? newText : ''));

    const slices: string[] = [];
    let cursor = 0;
    counts.forEach((c, i) => {
        if (i === counts.length - 1) {
            slices.push(words.slice(cursor).join(''));
        } else {
            const remaining = counts.length - 1 - i;
            const take = Math.max(
                0,
                Math.min(
                    Math.round((c / totalOriginal) * words.length),
                    words.length - cursor - remaining
                )
            );
            slices.push(words.slice(cursor, cursor + take).join(''));
            cursor += take;
        }
    });
    return slices;
};

/**
 * Replace <w:t> text within a single XML part string.
 *
 * Phase 1 — DOMParser reads structure (runs, rPr, word counts).
 * Phase 2 — String-based replacement writes the output. XMLSerializer is never
 *            used for output because it changes namespace prefixes (w: → ns1:),
 *            which causes Word's insertOoxml to fail its internal assertion.
 *
 * Three-path algorithm:
 *   Path A — single <w:t>: replace directly.
 *   Path B — all runs share identical <w:rPr>: put text in first, clear rest.
 *   Path C — mixed <w:rPr>: distribute new text proportionally by word count.
 */
const replaceInDocumentPart = (xmlString: string, newText: string): string | null => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'application/xml');

    if (doc.querySelector('parsererror')) {
        console.warn('[ooxmlHelper] OOXML parse error');
        return null;
    }

    // Positional work is driven by the regex matches, never by the DOM node list:
    // one run can hold several <w:t> nodes, so runs and <w:t> elements are not
    // interchangeable, and an index built from one list corrupts the other.
    const tMatches = Array.from(xmlString.matchAll(wTRegex()));
    if (tMatches.length === 0) {
        console.warn('[ooxmlHelper] No <w:t> elements found');
        return null;
    }

    // Path A — single <w:t>
    if (tMatches.length === 1) {
        let done = false;
        return xmlString.replace(wTRegex(), (original, open, _content, close) => {
            if (done) return original;
            done = true;
            return `${withPreserve(open)}${escapeXml(newText)}${close}`;
        });
    }

    const runs = Array.from(doc.getElementsByTagNameNS(WNS, 'r'));
    const runsWithText = runs.filter(r => r.getElementsByTagNameNS(WNS, 't').length > 0);

    // Path B — uniform formatting (all rPr identical or absent)
    const rPrSet = new Set(runsWithText.map(serializeRpr));
    if (rPrSet.size <= 1) {
        let first = true;
        return xmlString.replace(wTRegex(), (match, open, _content, close) => {
            if (first) {
                first = false;
                return `${withPreserve(open)}${escapeXml(newText)}${close}`;
            }
            return `${open}${close}`;
        });
    }

    // Path C — mixed formatting: distribute words proportionally.
    // One slice per <w:t> match (not per run): a run holding two <w:t> nodes would
    // otherwise shift every later slice and empty the trailing runs.
    const originalCounts = tMatches.map(m => (m[2].match(/\S+/g) ?? []).length);
    const slices = distributeByWordCount(newText, originalCounts).map(escapeXml);

    let sliceIdx = 0;
    return xmlString.replace(wTRegex(), (_match, open, _content, close) => {
        const slice = slices[sliceIdx] ?? '';
        sliceIdx++;
        return `${withPreserve(open)}${slice}${close}`;
    });
};

/**
 * Replace text content within an OOXML string while preserving run properties.
 *
 * When the input is a flat-OPC package (the format returned by range.getOoxml()),
 * text replacement is scoped exclusively to the /word/document.xml part.
 * This prevents new text from bleeding into word/comments.xml, word/footnotes.xml,
 * or word/endnotes.xml — all of which also contain <w:t> elements that would
 * otherwise absorb words and cause the "some text in document, some in comment" bug.
 * All other parts (including the comments and their relationship entries) are left
 * completely intact, so the flat-OPC package remains structurally valid.
 *
 * @param ooxmlString - The original OOXML package string from range.getOoxml()
 * @param newText - The replacement plain text
 * @returns Modified OOXML string, or null if replacement could not be performed
 */
export const replaceTextInOoxml = (
    ooxmlString: string,
    newText: string,
    fontName?: string | null,
): string | null => {
    try {
        // Detect flat-OPC wrapper and scope processing to the document body part only.
        // This keeps all other parts (comments, footnotes, styles, rels) untouched so
        // the package remains valid when passed to range.insertOoxml().
        const docPartRx = /(<pkg:part(?=[^>]*pkg:name="\/word\/document\.xml")[^>]*>)([\s\S]*?)(<\/pkg:part>)/i;
        const m = ooxmlString.match(docPartRx);

        if (m && m.index !== undefined) {
            const before      = ooxmlString.slice(0, m.index);
            const openTag     = m[1];
            const partContent = m[2];
            const closeTag    = m[3];
            const after       = ooxmlString.slice(m.index + m[0].length);

            const processed = replaceInDocumentPart(partContent, newText);
            if (processed === null) return null;
            const withExplicitFont =
                fontName && fontName.trim() && fontName !== 'Mixed'
                    ? applyExplicitFontToRuns(processed, fontName)
                    : processed;
            return before + openTag + withExplicitFont + closeTag + after;
        }

        // No flat-OPC wrapper (e.g. raw fragment) — process the whole string directly.
        const processed = replaceInDocumentPart(ooxmlString, newText);
        if (processed === null) return null;
        return fontName && fontName.trim() && fontName !== 'Mixed'
            ? applyExplicitFontToRuns(processed, fontName)
            : processed;

    } catch (err) {
        console.warn('[ooxmlHelper] replaceTextInOoxml failed:', err);
        return null;
    }
};

/**
 * Insert OOXML content into a range.
 */
export const insertOoxml = async (
    context: Word.RequestContext,
    range: Word.Range,
    ooxmlString: string,
): Promise<boolean> => {
    try {
        range.insertOoxml(ooxmlString, Word.InsertLocation.replace);
        await context.sync();
        return true;
    } catch (err) {
        console.warn('[ooxmlHelper] insertOoxml failed:', err);
        return false;
    }
};
